import * as R from 'ramda';
import { handleActions } from 'redux-actions';
import { v4 as uuid } from 'uuid';
import initialState, { getNewRule } from './initialState';
import {
  mapEligibilityOperators,
  mapSystemVariables,
  processPopulationChange,
  getLookupData,
} from './helpers';
import {
  getEligibilityOperators,
  getSystemVariables,
  getVariablesLookupData,
  getFormulaOperators,
  populationChange,
  ruleChange,
  addRuleBlockAction,
  duplicateRuleBlockAction,
  deleteRuleBlockAction,
  disclaimerFieldChange,
  setErrors,
  resetPopulations,
  resetRules,
  storeReset,
  bonusModellerSave,
  etagUpdate,
  bonusModellerPublished,
  bonusModellerLoaded,
} from '../actions';

const reducer = handleActions(
  new Map([
    [
      getEligibilityOperators,
      (state, action) => ({
        ...state,
        eligibilityOperators: mapEligibilityOperators(R.prop('payload', action)),
      }),
    ],
    [
      getSystemVariables,
      (state, action) => ({
        ...state,
        systemVariables: mapSystemVariables(R.prop('payload', action)),
      }),
    ],
    [
      getVariablesLookupData,
      (state, action) => {
        const { lookupData, url } = action.payload;

        return {
          ...state,
          lookUpValues: {
            ...state.lookUpValues,
            ...getLookupData(lookupData, url),
          },
        };
      },
    ],
    [
      populationChange,
      (state, action) => ({
        ...state,
        populations: processPopulationChange(R.clone(state.populations), R.prop('payload', action)),
      }),
    ],
    [
      getFormulaOperators,
      (state, action) => ({
        ...state,
        formulaOperators: R.prop('payload', action),
      }),
    ],
    [
      ruleChange,
      (state, { payload }) => ({
        ...state,
        [payload.rulesKey]: processPopulationChange(R.clone(state[payload.rulesKey]), payload),
      }),
    ],
    [
      addRuleBlockAction,
      (state, { payload: { rulesKey, index } }) => ({
        ...state,
        [rulesKey]: R.insert(index + 1, getNewRule(), R.clone(state[rulesKey])),
      }),
    ],
    [
      duplicateRuleBlockAction,
      (state, { payload: { rulesKey, index, rule } }) => ({
        ...state,
        [rulesKey]: R.insert(index + 1, { ...R.clone(rule), key: uuid() }, R.clone(state[rulesKey])),
      }),
    ],
    [
      deleteRuleBlockAction,
      (state, { payload: { rulesKey, index } }) => ({
        ...state,
        [rulesKey]: R.remove(index, 1, R.clone(state[rulesKey])),
      }),
    ],
    [
      resetPopulations,
      (state) => ({
        ...state,
        populations: R.clone(initialState.populations),
        errors: { ...state.errors, populations: R.clone(initialState.errors.populations) },
      }),
    ],
    [
      resetRules,
      (state, { payload: rulesKey }) => ({
        ...state,
        [rulesKey]: [getNewRule()],
        errors: { ...state.errors, [rulesKey]: R.clone(initialState.errors[rulesKey]) },
      }),
    ],
    [
      disclaimerFieldChange,
      (state, { payload: { field, value } }) => ({
        ...state,
        [field]: value,
      }),
    ],
    [
      setErrors,
      (state, { payload: { field, value } }) => ({
        ...state,
        errors: { ...state.errors, [field]: value },
      }),
    ],
    [storeReset, () => R.clone(initialState)],
    [
      bonusModellerSave,
      (state, { payload: { response, id } }) => {
        // the server assigns a real population id on first save; without merging it back in,
        // a still-placeholder id (see DEFAULT_POPULATIONS) would be sent on the next update
        const record = R.propOr(response, 'response', response);
        const savedIds = (R.prop('eligiblePopulationRules', record) || []).map(R.prop('id'));
        const populations = savedIds.length
          ? state.populations.map((population, index) =>
              savedIds[index] != null ? { ...population, id: savedIds[index] } : population
            )
          : state.populations;

        return {
          ...state,
          populations,
          savedId: id,
          status: R.prop('status', record) || state.status,
        };
      },
    ],
    [
      etagUpdate,
      (state, { payload: { etag } }) => ({
        ...state,
        savedEtag: etag || state.savedEtag,
      }),
    ],
    [bonusModellerPublished, (state, { payload }) => ({
      ...state,
      status: R.prop('status', payload) || state.status,
    })],
    [
      bonusModellerLoaded,
      (state, { payload: { id, ...loadedData } }) => ({
        ...state,
        ...loadedData,
        errors: R.clone(initialState.errors),
        savedId: id || state.savedId,
      }),
    ],
  ]),
  R.clone(initialState)
);

export default reducer;
